import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";

import { Heading } from "../../components/Heading";
import GroupFormFields from "../../components/GroupFormData";
import { useUpdateGroupMutation } from "../../api/mutations/useUpdateGroupMutation";

import { groupFormSchema } from "../../schema/group.schema";
import { GroupFormData } from "../../types/group.types";
import { toast } from "react-toastify";
import { getEntityProfileRoute } from "../../../../routers/routeUtils";
import { useGroupDetailsQuery } from "../../api/queries/useGroupDetailsQuery";

const GroupEntityDetails = () => {
  const { t } = useTranslation("groups");
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const updateGroupMutation = useUpdateGroupMutation();

  const { groupId } = useParams<{ groupId: string }>();
  const { data: group } = useGroupDetailsQuery(Number(entityId), Number(groupId));

  const methods = useForm<GroupFormData>({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      name: "",
      description: "",
      imageLink: "",
      users: [],
      groupPrivacy: "public",
    },
  });

  useEffect(() => {
    if (group) {
      methods.reset({
        name: group.name,
        description: group.description,
        imageLink: group.imageLink,
        users: group.users.map((user) => user.id),
        groupPrivacy: "public",
      });
    }
  }, [group, methods]);

  const onSubmit = async (data: GroupFormData) => {
    try {
      await updateGroupMutation.mutateAsync({
        entityId: Number(entityId),
        groupId: Number(groupId),
        data,
      });
      toast.success(t("groupUpdatedSuccessfully"));
      setTimeout(() => {
        navigate(`${getEntityProfileRoute(Number(entityId))}?tab=groups`);
      }, 1000);
    } catch (error) {
      toast.error(t("failedToUpdateGroup"));
      console.error("Failed to update group:", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: "#FFF",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "32px",
      }}
    >
      <Heading title={t("editGroup")} />
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px" }}
        >
          <GroupFormFields />

          <Box
            sx={{
              padding: "0 16px 12px",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button variant="primary-sm" type="submit" disabled={updateGroupMutation.isPending}>
              {t("saveGroup")}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default GroupEntityDetails;
